/**
 * Builds the 8-bit sRGB "colour" view of a frame for display only: linear
 * light (after dark/flat correction) -> sRGB primaries -> exposure -> sRGB
 * encoding. Measurements never read this buffer.
 */
import { srgbOetf, toLinearSrgbMatrix } from '../core/color';
import { linearAt, type PipelineContext } from '../core/pipeline';

/** Reference white for absolute (PQ / HLG) data, BT.2408: 203 cd/m². */
export const HDR_REFERENCE_WHITE = 203;

let encodeLut: Uint8Array | null = null;
function srgbEncodeLut(): Uint8Array {
  if (encodeLut) return encodeLut;
  encodeLut = new Uint8Array(4097);
  for (let i = 0; i <= 4096; i++) encodeLut[i] = Math.round(srgbOetf(i / 4096) * 255);
  return encodeLut;
}

export function buildColorRGBA(ctx: PipelineContext, exposureEV: number): Uint8Array {
  const f = ctx.frame;
  const n = f.width * f.height;
  const out = new Uint8Array(n * 4);
  const s = ctx.settings;
  const identityPath =
    exposureEV === 0 &&
    !f.isFloat &&
    f.maxCode === 255 &&
    ctx.enc.transfer === 'srgb' &&
    ctx.enc.primaries === 'srgb' &&
    s.darkMode === 'none' &&
    !ctx.flat;
  if (identityPath) {
    // Exact code values: the file is already sRGB-encoded.
    const d = f.data;
    if (f.channels === 3)
      for (let i = 0; i < n; i++) {
        out[i * 4] = d[i * 3];
        out[i * 4 + 1] = d[i * 3 + 1];
        out[i * 4 + 2] = d[i * 3 + 2];
        out[i * 4 + 3] = 255;
      }
    else
      for (let i = 0; i < n; i++) {
        out[i * 4] = out[i * 4 + 1] = out[i * 4 + 2] = d[i];
        out[i * 4 + 3] = 255;
      }
    return out;
  }
  const m = toLinearSrgbMatrix(ctx.enc.rgbToXyz, ctx.enc.chromaticities.w);
  const k = Math.pow(2, exposureEV) / (ctx.enc.absolute ? HDR_REFERENCE_WHITE : 1);
  const lut = srgbEncodeLut();
  const rgb = [0, 0, 0];
  const enc = (v: number) => {
    if (!(v > 0)) return 0;
    if (v >= 1) return 255;
    return lut[(v * 4096 + 0.5) | 0];
  };
  for (let i = 0; i < n; i++) {
    linearAt(ctx, i, rgb);
    const r = rgb[0] * k;
    const g = rgb[1] * k;
    const b = rgb[2] * k;
    out[i * 4] = enc(m[0] * r + m[1] * g + m[2] * b);
    out[i * 4 + 1] = enc(m[3] * r + m[4] * g + m[5] * b);
    out[i * 4 + 2] = enc(m[6] * r + m[7] * g + m[8] * b);
    out[i * 4 + 3] = 255;
  }
  return out;
}
